import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Button, FormControl, ListGroup } from 'react-bootstrap'
import * as s from './EliminatorRegisterPlayers.scss'
import { getRegistrations, updateRegistrationStatus, updateRegistrationPayment } from '@severed-links/common.redherrings-reducers/eliminatorAdmin'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import { includes, lowerCase, trim } from 'lodash'
import PlayerListItem from '../../Player/PlayerListItem'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import SeasonSelector from '../../Football/SeasonSelector'

const RegisterPlayerButton = ({ title = '', isChecked = false, disabled = false, onClick }) => (
    <Button size='sm' className={s.registrationButton} disabled={disabled}
        onClick={() => onClick(!isChecked)} variant={disabled ? 'light' : isChecked ? 'success' : 'danger'}>
        <FontAwesomeIcon className={s.buttonIcon} name={isChecked ? 'check' : 'times'} />
        <div className={s.buttonTitle}>{!isChecked ? 'not ' : ''}{title}</div>
    </Button>
)

const EliminatorRegisterPlayers = () => {

    const dispatch = useDispatch()
    const params = useParams()
    const seasons = useSelector(state => state.eliminator.seasons || [])
    const seasonName = params.seasonName || null
    const season = seasons.find(i => i.seasonName === seasonName)
    const seasonId = (season || {}).seasonId || null
    const [registrations, setRegistrations] = useState({})
    const [filterText, setFilterText] = useState('')

    useEffect(() => { doGetRegistrations() }, [seasonName])

    const updateRegistration = (playerId, isRegistered) => {
        var _registrations = { ...registrations }
        var index = _registrations.players.findIndex(p => p.playerId === playerId)
        _registrations.players[index].isRegistered = isRegistered
        _registrations.players[index].isRegisteredNotPaid = isRegistered && !_registrations.players[index].isPaid
        setRegistrations(_registrations)
        dispatch(updateRegistrationStatus(seasonId, playerId, isRegistered))
        .then(resp => dispatch(createNotification({ 
            type: resp.payload.messageType || 'error', 
            headline: 'Update Player Registration Status', 
            message: resp.payload.message || resp.message, 
            timeout: 6000 
        })))
    }

    const updatePayment = (playerId, isPaid) => {
        var _registrations = { ...registrations }
        var index = _registrations.players.findIndex(p => p.playerId === playerId)
        _registrations.players[index].isPaid = isPaid
        _registrations.players[index].isRegisteredNotPaid = _registrations.players[index].isRegistered && !_registrations.players[index].isPaid
        setRegistrations(_registrations)
        dispatch(updateRegistrationPayment(seasonId, playerId, isPaid))
        .then(resp => dispatch(createNotification({ 
            type: resp.payload.messageType || 'error', 
            headline: 'Update Player Registration Payment', 
            message: resp.payload.message || resp.message, 
            timeout: 6000 
        })))
    }

    const doGetRegistrations = () => {
        if (seasonId) {
            setRegistrations([])
            dispatch(getRegistrations(seasonId))
            .then(action => setRegistrations(action.payload))
        }
    }

    const players = registrations && registrations.players ? registrations.players : []
    const normalizedFilterText = trim(lowerCase(filterText))
    const filteredPlayers = !normalizedFilterText ? players : players.filter(p => {
        const searchableName = lowerCase(`${p.firstName || ''} ${p.lastName || ''} ${p.fullName || ''}`)
        return includes(searchableName, normalizedFilterText)
    })
    const registeredCount = players.filter(p => p.isRegistered).length
    const paidCount = players.filter(p => p.isPaid).length
    const unpaidCount = players.filter(p => p.isRegisteredNotPaid).length

    return (
        <div className={s.container}>
            <section className={s.pageHeader}>
                <div className={s.headerCopy}>
                    <div className={s.eyebrow}>Eliminator Admin</div>
                    <h2 className={s.title}>Register Players</h2>
                    <div className={s.subtitle}>{seasonName} manual registration and payment status</div>
                </div>
                <div className={s.headerActions}>
                    <SeasonSelector seasons={seasons} season={season} />
                </div>
            </section>

            <section className={s.summaryGrid}>
                <div className={s.summaryPill}>
                    <span>{players.length}</span>
                    players
                </div>
                <div className={s.summaryPill}>
                    <span>{registeredCount}</span>
                    registered
                </div>
                <div className={s.summaryPill}>
                    <span>{paidCount}</span>
                    paid
                </div>
                <div className={s.summaryPill + (unpaidCount ? ` ${s.warning}` : '')}>
                    <span>{unpaidCount}</span>
                    registered, unpaid
                </div>
            </section>

            <section className={s.filterBar}>
                <div>
                    <label htmlFor='eliminator-registration-player-filter'>Find Player</label>
                    <div className={s.filterInput}>
                        <FontAwesomeIcon name='magnifying-glass' />
                        <FormControl id='eliminator-registration-player-filter'
                            placeholder='Type a first or last name'
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)} />
                        {filterText ?
                        <Button variant='light' title='clear filter' onClick={() => setFilterText('')}>
                            <FontAwesomeIcon name='xmark' />
                        </Button>
                        : null}
                    </div>
                </div>
                <div className={s.filterCount}>
                    {filteredPlayers.length} of {players.length} shown
                </div>
            </section>

            <section className={s.registrationCard}>
                <div className={s.listHeader}>
                    <div>Player</div>
                    <div>Status</div>
                </div>
                <ListGroup className={s.registrationList}>
                {filteredPlayers.map(p =>
                <PlayerListItem player={p}
                    className={s.playerRow}
                    rightDetail={<div className={s.rightDetail}>
                        <RegisterPlayerButton title='registered' isChecked={p.isRegistered}
                            onClick={_bool => updateRegistration(p.playerId, _bool)} />
                        <RegisterPlayerButton title='paid' isChecked={p.isPaid} disabled={!p.isRegistered}
                            onClick={_bool => updatePayment(p.playerId, _bool)} />
                    </div>}
                    key={`registration-${seasonName}-player-${p.playerId}`} />
                )}
                {!filteredPlayers.length ?
                <div className={s.emptyState}>No players match that filter.</div>
                : null}
                </ListGroup>
            </section>
        </div>
    )
}

export default EliminatorRegisterPlayers
